import { useLazyQuery } from '@apollo/client'
import { FocusEventHandler, ReactElement, ReactNode, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { SelectTags } from 'components/v2/SelectTags'
import { GET_USERS_SUGGESTIONS } from 'features/Users/queries-legacy'
import { GTUserSuggestion, GTUserSuggestionsResponse } from 'gql/types.generated'
import { useDebouncedQuery } from 'components/v2/Autocomplete'
import { EUserType } from 'features/Users/types'

interface IMultiUserAutocompleteProps {
  companyId?: string
  id?: string
  isAssigned?: boolean
  isActive?: boolean
  selectedUsers: GTUserSuggestion[]
  onChange: (users: GTUserSuggestion[]) => void
  onBlur?: FocusEventHandler<HTMLInputElement>
  type?: EUserType
  skip?: boolean
  optional?: boolean
  label?: ReactNode
}

export const MultiUserAutocomplete = ({
  companyId,
  isAssigned,
  isActive,
  selectedUsers,
  id,
  onChange,
  onBlur,
  type,
  skip = false,
  optional = false,
  label
}: IMultiUserAutocompleteProps): ReactElement | null => {
  const [pattern, setPattern] = useState('')
  const [getUsers, { data, loading, error }] = useLazyQuery<{ userSuggestions: GTUserSuggestionsResponse }>(
    GET_USERS_SUGGESTIONS
  )
  const { isDebouncing } = useDebouncedQuery({
    query: getUsers,
    pattern,
    extraInputs: {
      companyID: companyId,
      isAssigned,
      isActive,
      type
    },
    skip
  })

  const selectedUsersId = selectedUsers.map(({ id }) => id)
  const filteredUsers =
    data?.userSuggestions.items.filter((user) => {
      return !selectedUsersId.includes(user.id)
    }) || []

  const handleChange = (users: GTUserSuggestion[], pattern = ''): void => {
    setPattern(pattern)

    onChange(users)
  }

  return (
    <SelectTags
      id={id}
      values={selectedUsers}
      items={filteredUsers}
      getTagLabel={({ firstName, lastName, username }): string => `${firstName} ${lastName} (${username})`}
      onChange={handleChange}
      onBlur={onBlur}
      label={label ? label : <FormattedMessage id="dictionary.users" defaultMessage="Users" />}
      emptyMessage={<FormattedMessage id="siteUsers.autocomplete.empty" defaultMessage="No users found" />}
      loading={loading || isDebouncing}
      error={error}
      optional={optional}
    />
  )
}
